import React, { useState, useRef } from 'react';
import AdminDashboardLayout from './AdminDashboardLayout';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { changePasswordAPI } from '../../redux/slices/ChnagePasswordSlice';
import { updateAdminProfileAPI } from '../../redux/slices/AdminProfileSlice';
import { updateUser } from '../../redux/slices/authSlice';
import { toast } from 'react-toastify';
import { errorHandler, base } from '../../constants/base';
import './AdminSettings.css';

const AdminSettings = () => {
    const dispatch = useDispatch();
    const loading = useSelector(state => state.ChnagePasswordSlice.loading);
    const profileLoading = useSelector(state => state.AdminProfileSlice.loading);
    const user = useSelector(state => state.AuthSlice.user);

    const [activeTab, setActiveTab] = useState('profile');
    const [showOldPass, setShowOldPass] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);
    const [profilePreview, setProfilePreview] = useState(null);
    const [profileFile, setProfileFile] = useState(null);
    const fileInputRef = useRef(null);

    const passwordValidationSchema = yup.object().shape({
        old_password: yup
            .string()
            .required("Old password is required"),
        password: yup
            .string()
            .matches(
                /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                "Password must be at least 8 characters, include 1 uppercase, 1 number, and 1 special character"
            )
            .required("New password is required"),
        confirm_password: yup
            .string()
            .oneOf([yup.ref('password'), null], "Passwords must match")
            .required("Confirm password is required"),
    });

    const profileValidationSchema = yup.object().shape({
        name: yup
            .string()
            .min(3, "Name must be at least 3 characters")
            .required("Name is required"),
    });

    const handlePasswordSubmit = async (values, { resetForm }) => {
        const res = await dispatch(changePasswordAPI(values));
        if (changePasswordAPI.fulfilled.match(res)) {
            if (res.payload.success || res.payload.status === 200) {
                toast.success("Password updated successfully");
                resetForm();
            } else {
                errorHandler(res.payload.errors);
            }
        } else if (changePasswordAPI.rejected.match(res)) {
            errorHandler(res.payload.errors);
        } else {
            toast.error('Sorry, something went wrong!');
        }
    };

    const handleProfileSubmit = async (values) => {
        const formData = new FormData();
        formData.append('id', user?.id);
        formData.append('name', values.name);

        if (profileFile) {
            formData.append('profile', profileFile);
        }

        const res = await dispatch(updateAdminProfileAPI(formData));
        if (updateAdminProfileAPI.fulfilled.match(res)) {
            if (res.payload.data.success || res.payload.status === 200) {
                dispatch(updateUser(res.payload.data.data));
                toast.success("Profile updated successfully");
                setProfileFile(null);
                setProfilePreview(null);
            } else {
                errorHandler(res.payload.data.errors);
            }
        } else if (updateAdminProfileAPI.rejected.match(res)) {
            errorHandler(res.payload.errors);
        } else {
            toast.error('Sorry, something went wrong!');
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error("Image size should be less than 5MB");
                return;
            }
            setProfileFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const getProfileImage = () => {
        if (profilePreview) return profilePreview;
        if (user?.images?.[0]?.path) return `${base.imageUrl}${user.images[0].path}`;
        return null;
    };

    const menuItems = [
        { id: 'profile', label: 'Profile', icon: 'fa-solid fa-user' },
        { id: 'password', label: 'Change Password', icon: 'fa-solid fa-lock' },
        { id: 'support', label: 'Support', icon: 'fa-solid fa-headset' },
    ];

    return (
        <AdminDashboardLayout>
            <div className="admin-settings">
                <h1 className="admin-page-title">Settings</h1>

                <div className="admin-settings-container">
                    {/* Settings Menu */}
                    <div className="admin-settings-menu">
                        <div className="settings-menu-card">
                            <h3 className="settings-menu-title">Menu</h3>
                            <ul className="settings-menu-list">
                                {menuItems.map((item) => (
                                    <li
                                        key={item.id}
                                        className={`settings-menu-item ${activeTab === item.id ? 'active' : ''}`}
                                        onClick={() => setActiveTab(item.id)}
                                    >
                                        <i className={item.icon}></i>
                                        <span>{item.label}</span>
                                        <i className="fa-solid fa-chevron-right arrow-icon"></i>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Settings Content */}
                    <div className="admin-settings-content">
                        {activeTab === 'profile' && (
                            <div className="settings-content-card">
                                <div className="settings-card-header">
                                    <div className="settings-card-icon">
                                        <i className="fa-solid fa-user"></i>
                                    </div>
                                    <div>
                                        <h2 className="settings-card-title">Profile Settings</h2>
                                        <p className="settings-card-subtitle">Update your profile information</p>
                                    </div>
                                </div>

                                <Formik
                                    initialValues={{
                                        name: user?.name || '',
                                    }}
                                    validationSchema={profileValidationSchema}
                                    onSubmit={handleProfileSubmit}
                                    enableReinitialize
                                >
                                    {() => (
                                        <Form className="settings-form profile-form">
                                            <div className="profile-avatar-section">
                                                <div className="profile-avatar-wrapper" onClick={handleImageClick}>
                                                    {getProfileImage() ? (
                                                        <img
                                                            src={getProfileImage()}
                                                            alt={user?.name}
                                                            className="profile-avatar-img"
                                                        />
                                                    ) : (
                                                        <div className="profile-avatar-placeholder">
                                                            <i className="fas fa-user"></i>
                                                        </div>
                                                    )}
                                                    <div className="profile-avatar-overlay">
                                                        <i className="fas fa-camera"></i>
                                                    </div>
                                                </div>
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    onChange={handleFileChange}
                                                    accept="image/jpeg,image/png,image/jpg"
                                                    style={{ display: 'none' }}
                                                />
                                                <p className="profile-avatar-hint">Click to change profile picture</p>
                                            </div>

                                            <div className="settings-form-group">
                                                <label className="settings-form-label">Full Name</label>
                                                <div className="settings-input-wrapper">
                                                    <Field
                                                        type="text"
                                                        name="name"
                                                        placeholder="Enter your name"
                                                        className="settings-form-input"
                                                    />
                                                    <i className="fa-solid fa-user input-icon-static"></i>
                                                </div>
                                                <ErrorMessage name="name" component="div" className="settings-error" />
                                            </div>

                                            <div className="settings-form-group">
                                                <label className="settings-form-label">Email Address</label>
                                                <div className="settings-input-wrapper">
                                                    <input
                                                        type="email"
                                                        value={user?.email || ''}
                                                        className="settings-form-input"
                                                        disabled
                                                    />
                                                    <i className="fa-solid fa-envelope input-icon-static"></i>
                                                </div>
                                                <p className="settings-field-hint">Email cannot be changed</p>
                                            </div>

                                            <div className="settings-form-actions">
                                                <button type="submit" className="settings-submit-btn" disabled={profileLoading}>
                                                    {profileLoading ? (
                                                        <span className="settings-btn-loader"></span>
                                                    ) : (
                                                        <>
                                                            <i className="fa-solid fa-check"></i>
                                                            Save Changes
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        )}

                        {activeTab === 'password' && (
                            <div className="settings-content-card">
                                <div className="settings-card-header">
                                    <div className="settings-card-icon">
                                        <i className="fa-solid fa-lock"></i>
                                    </div>
                                    <div>
                                        <h2 className="settings-card-title">Change Password</h2>
                                        <p className="settings-card-subtitle">Update your account password</p>
                                    </div>
                                </div>

                                <Formik
                                    initialValues={{
                                        old_password: '',
                                        password: '',
                                        confirm_password: '',
                                        email: user?.email
                                    }}
                                    validationSchema={passwordValidationSchema}
                                    onSubmit={handlePasswordSubmit}
                                >
                                    {() => (
                                        <Form className="settings-form">
                                            <div className="settings-form-group">
                                                <label className="settings-form-label">Current Password</label>
                                                <div className="settings-input-wrapper">
                                                    <Field
                                                        type={showOldPass ? "text" : "password"}
                                                        name="old_password"
                                                        placeholder="Enter current password"
                                                        className="settings-form-input"
                                                    />
                                                    <i
                                                        className={`fa-solid ${showOldPass ? 'fa-eye-slash' : 'fa-eye'} input-icon`}
                                                        onClick={() => setShowOldPass(!showOldPass)}
                                                    ></i>
                                                </div>
                                                <ErrorMessage name="old_password" component="div" className="settings-error" />
                                            </div>

                                            <div className="settings-form-group">
                                                <label className="settings-form-label">New Password</label>
                                                <div className="settings-input-wrapper">
                                                    <Field
                                                        type={showPass ? "text" : "password"}
                                                        name="password"
                                                        placeholder="Enter new password"
                                                        className="settings-form-input"
                                                    />
                                                    <i
                                                        className={`fa-solid ${showPass ? 'fa-eye-slash' : 'fa-eye'} input-icon`}
                                                        onClick={() => setShowPass(!showPass)}
                                                    ></i>
                                                </div>
                                                <ErrorMessage name="password" component="div" className="settings-error" />
                                            </div>

                                            <div className="settings-form-group">
                                                <label className="settings-form-label">Confirm New Password</label>
                                                <div className="settings-input-wrapper">
                                                    <Field
                                                        type={showConfirmPass ? "text" : "password"}
                                                        name="confirm_password"
                                                        placeholder="Confirm new password"
                                                        className="settings-form-input"
                                                    />
                                                    <i
                                                        className={`fa-solid ${showConfirmPass ? 'fa-eye-slash' : 'fa-eye'} input-icon`}
                                                        onClick={() => setShowConfirmPass(!showConfirmPass)}
                                                    ></i>
                                                </div>
                                                <ErrorMessage name="confirm_password" component="div" className="settings-error" />
                                            </div>

                                            <div className="settings-form-actions">
                                                <button type="submit" className="settings-submit-btn" disabled={loading}>
                                                    {loading ? (
                                                        <span className="settings-btn-loader"></span>
                                                    ) : (
                                                        <>
                                                            <i className="fa-solid fa-check"></i>
                                                            Update Password
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        )}

                        {activeTab === 'support' && (
                            <div className="settings-content-card">
                                <div className="settings-card-header">
                                    <div className="settings-card-icon">
                                        <i className="fa-solid fa-headset"></i>
                                    </div>
                                    <div>
                                        <h2 className="settings-card-title">Support</h2>
                                        <p className="settings-card-subtitle">Get help and contact us</p>
                                    </div>
                                </div>

                                <div className="support-content">
                                    <div className="support-info-card">
                                        <div className="support-info-icon">
                                            <i className="fa-solid fa-building"></i>
                                        </div>
                                        <div className="support-info-text">
                                            <h4>I-Sit Corporation</h4>
                                            <p>We're here to help you with any questions or concerns</p>
                                        </div>
                                    </div>

                                    <div className="support-contact-list">
                                        <div className="support-contact-item">
                                            <div className="support-contact-icon">
                                                <i className="fa-solid fa-envelope"></i>
                                            </div>
                                            <div className="support-contact-info">
                                                <span className="support-contact-label">Email</span>
                                                <a href="mailto:support@isit.com" className="support-contact-value">support@isit.com</a>
                                            </div>
                                        </div>

                                        <div className="support-contact-item">
                                            <div className="support-contact-icon">
                                                <i className="fa-solid fa-globe"></i>
                                            </div>
                                            <div className="support-contact-info">
                                                <span className="support-contact-label">Website</span>
                                                <a href="https://isit.services" target="_blank" rel="noopener noreferrer" className="support-contact-value">isit.services</a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AdminDashboardLayout>
    );
};

export default AdminSettings;
